/**
 * @module utils/index
 * @description Main utilities barrel export. Re-exports date, package, and string utilities,
 * plus provides browser-specific helper functions for links and clipboard operations.
 */

export * from './date';
export * from './package';
export * from './str';

/**
 * @const REACT_TOOLTIP_Z_INDEX
 * @description Z-index value for React tooltip positioning
 * @type {number}
 */
export const REACT_TOOLTIP_Z_INDEX = 1000;

/**
 * @function openLink
 * @description Open a URL in a new browser tab/window
 * @param {string} url - Destination URL to open
 * @returns {function(): void} Click handler function that opens the URL
 */
export const openLink = (url: string): (() => void) => () => {
  window.open(url, '_blank');
};

/**
 * @function copyPassword
 * @description Copy provided text to the system clipboard using the Clipboard API
 * @param {string} text - Value to copy to clipboard
 * @returns {function(): Promise<void>} Click handler function that copies text and resolves when done
 */
export const copyPassword = (text: string): (() => Promise<void>) => () => {
  return navigator.clipboard.writeText(text);
};

/**
 * @function emailLink
 * @description Create a link to an email with a subject
 * @param {string} subject - The subject of the email
 * @param {string} email - The email address (optional, defaults to `process.env.REACT_APP_SUPPORT_EMAIL || process.env.SUPPORT_EMAIL`)
 * @returns {string} The link to the email
 */
export const emailLink = (subject: string, email = process.env.REACT_APP_SUPPORT_EMAIL || process.env.SUPPORT_EMAIL): string => {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}`;
};

/**
 * @function onlyNonEmpty
 * @description Filter out empty/undefined values from an object
 * @param {Record<string, string | undefined>} obj - The object to filter
 * @returns {Partial<Record<string, string | undefined>>} The filtered object
 */
export const onlyNonEmpty = <T extends Record<string, string | undefined>>(obj: T): Partial<T> =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value && value.trim() !== '') {
      acc[key as keyof T] = value as T[keyof T];
    }
    return acc;
  }, {} as Partial<T>);

/**
 * @function cascade
 * @description Get first non-empty value from cascade
 * @param {...T | undefined | null} values - The values to cascade
 * @returns {T | undefined} The first non-empty value
 */
export const cascade = <T>(...values: (T | undefined | null)[]): T | undefined => {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};
